import React from 'react';
import { Helmet } from 'react-helmet-async';

function PrivacyPage() {
  return (
    <div className="min-h-[calc(100vh-128px)] flex flex-col items-center text-white">
      <Helmet>
        <title>Privacy Policy — CommunityGuardAPI</title>
        <link rel="canonical" href="https://communityguardapi.com/privacy" />
        <meta name="description" content="Privacy policy for CommunityGuardAPI." />
        <meta property="og:title" content="Privacy Policy — CommunityGuardAPI" />
        <meta property="og:description" content="Privacy policy for CommunityGuardAPI." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/privacy" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <div className="w-full bg-gradient-to-b from-slate-900 to-slate-800 py-8 px-4 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-3">Privacy Policy</h1>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto">
          Last updated: March 2026
        </p>
      </div>

      <div className="w-full max-w-4xl px-4 py-8 space-y-8">
        {/* Introduction */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
          <p className="text-slate-300 leading-relaxed">
            CommunityGuardAPI is operated by Asan Digital LLC ("we," "us," or "our"). This Privacy
            Policy describes how we collect, use, disclose, and protect your personal information
            when you access or use our website, applications, and related services (collectively,
            the "Service"). By using the Service, you agree to the collection and use of
            information in accordance with this policy.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Account Information</h3>
              <p>
                When you register for an account, we collect your email address and name. This
                information is used to identify your account, provide you with access to the
                Service, and communicate with you regarding your account.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Usage Data</h3>
              <p>
                We collect information about how you interact with the Service, including API
                calls, search queries, and feature usage. This data is used for billing purposes,
                to monitor service performance, and to improve the overall quality of the Service.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Location Data</h3>
              <p>
                With your explicit permission, we may access your device's location data to
                provide the nearby offender search feature. Your location data is used solely for
                the purpose of returning relevant search results and is{' '}
                <span className="text-white font-medium">not stored on our servers</span>. You
                may revoke location permissions at any time through your browser or device
                settings.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Web Analytics</h3>
              <p>
                We collect standard web analytics data, including page views, device type, browser
                type, and referring URLs. This information helps us understand how users interact
                with the Service and enables us to make improvements.
              </p>
            </div>
          </div>
        </section>

        {/* How We Use Your Information */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
          <p className="text-slate-300 leading-relaxed mb-3">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside text-slate-300 space-y-2 leading-relaxed">
            <li>
              <span className="text-white font-medium">Account Management:</span> To create and
              maintain your account, authenticate your identity, and provide customer support.
            </li>
            <li>
              <span className="text-white font-medium">Billing:</span> To process payments,
              track usage against your subscription plan, and generate invoices.
            </li>
            <li>
              <span className="text-white font-medium">Service Improvement:</span> To analyze
              usage patterns, diagnose technical issues, and develop new features.
            </li>
            <li>
              <span className="text-white font-medium">Security Monitoring:</span> To detect,
              prevent, and respond to fraud, abuse, and other harmful activity.
            </li>
          </ul>
          <p className="text-slate-300 leading-relaxed mt-4 font-medium">
            We do <span className="text-red-400">NOT</span> sell your personal data to third
            parties.
          </p>
        </section>

        {/* Sex Offender Data */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">4. Sex Offender Data</h2>
          <p className="text-slate-300 leading-relaxed">
            All sex offender data displayed on the CommunityGuardAPI platform is sourced exclusively
            from publicly available government registries maintained by federal, state, and local
            authorities. We do not independently collect, verify, or create sex offender data. We
            function as an aggregator of public records, consolidating information from multiple
            jurisdictions into a single searchable interface for the convenience of our users. The
            accuracy and completeness of this data is dependent on the source registries, and we
            encourage users to verify information through official government sources.
          </p>
        </section>

        {/* Data Sharing */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">5. Data Sharing</h2>
          <p className="text-slate-300 leading-relaxed mb-3">
            We do not sell, rent, or trade your personal information to third parties. We may
            share your information only in the following limited circumstances:
          </p>
          <ul className="list-disc list-inside text-slate-300 space-y-2 leading-relaxed">
            <li>
              <span className="text-white font-medium">Payment Processors:</span> We share
              billing information with Stripe, our payment processing provider, solely for the
              purpose of processing your payments.
            </li>
            <li>
              <span className="text-white font-medium">Legal Compliance:</span> We may disclose
              your information to law enforcement or other governmental authorities if required to
              do so by law, regulation, or valid legal process (e.g., subpoena or court order).
            </li>
            <li>
              <span className="text-white font-medium">Service Providers:</span> We may share
              information with trusted third-party service providers who assist us in operating
              the platform, provided they are bound by confidentiality obligations and use the
              data only as directed by us.
            </li>
          </ul>
        </section>

        {/* Data Security */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">6. Data Security</h2>
          <p className="text-slate-300 leading-relaxed">
            We take the security of your personal information seriously and implement
            industry-standard measures to protect it. These measures include encrypted connections
            (TLS/SSL) for all data in transit, hashed API keys, secure password storage using
            modern hashing algorithms, and secure payment processing through PCI-compliant
            providers. While no method of transmission or storage is 100% secure, we strive to
            use commercially reasonable means to protect your information.
          </p>
        </section>

        {/* Cookies */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">7. Cookies</h2>
          <p className="text-slate-300 leading-relaxed">
            We use essential cookies that are strictly necessary for the operation of the Service.
            These cookies are used for authentication and session management, ensuring that you
            remain logged in and that your preferences are preserved during your session. We do{' '}
            <span className="text-white font-medium">not</span> use third-party tracking cookies,
            and we do not engage in cross-site tracking of our users.
          </p>
        </section>

        {/* Your Rights */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">8. Your Rights</h2>
          <div className="text-slate-300 leading-relaxed space-y-3">
            <p>
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <span className="text-white font-medium">Account Deletion:</span> You may
                request the deletion of your account and all associated personal data by
                contacting us at{' '}
                <a
                  href="mailto:info@asan.digital"
                  className="text-emerald-500 hover:underline"
                >
                  info@asan.digital
                </a>
                . We will process deletion requests within a reasonable timeframe.
              </li>
              <li>
                <span className="text-white font-medium">Communication Preferences:</span> You
                may opt out of non-essential communications (such as promotional emails) at any
                time by using the unsubscribe link in our emails or by contacting us directly.
              </li>
              <li>
                <span className="text-white font-medium">Data Access:</span> You may request a
                copy of the personal information we hold about you by contacting us at the email
                address provided below.
              </li>
            </ul>
          </div>
        </section>

        {/* Children's Privacy */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
          <p className="text-slate-300 leading-relaxed">
            The Service is not intended for use by individuals under the age of 18. We do not
            knowingly collect personal information from anyone under 18 years of age. If we become
            aware that we have collected personal information from a minor, we will take
            reasonable steps to delete that information promptly. If you believe that a minor has
            provided us with personal information, please contact us at{' '}
            <a
              href="mailto:info@asan.digital"
              className="text-emerald-500 hover:underline"
            >
              info@asan.digital
            </a>
            .
          </p>
        </section>

        {/* Changes to This Policy */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">10. Changes to This Policy</h2>
          <p className="text-slate-300 leading-relaxed">
            We reserve the right to update or modify this Privacy Policy at any time. When we make
            changes, we will revise the "Last updated" date at the top of this page. We encourage
            you to review this Privacy Policy periodically to stay informed about how we are
            protecting your information. Your continued use of the Service after any changes to
            this policy constitutes your acceptance of those changes.
          </p>
        </section>

        {/* Contact */}
        <section className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
          <p className="text-slate-300 leading-relaxed">
            If you have any questions, concerns, or requests regarding this Privacy Policy or our
            data practices, please contact us at:
          </p>
          <div className="mt-4 text-slate-300">
            <p className="font-medium text-white">Asan Digital LLC</p>
            <p>
              Email:{' '}
              <a
                href="mailto:info@asan.digital"
                className="text-emerald-500 hover:underline"
              >
                info@asan.digital
              </a>
            </p>
          </div>
        </section>

        <p className="text-center text-sm text-slate-500 pb-4">
          &copy; {new Date().getFullYear()} Asan Digital LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default PrivacyPage;
